import type {ReactElement} from 'react';
import {useState} from 'react';
import type {Block} from 'grapesjs';
import ClickableDiv from '@Components/clickable-div/clickable-div';
import {Text, TextSize} from '@Components/text';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {TextBlockPanel} from '@Components/email-maker-block-manager/components/text-block-panel';
import {ImageBlockPanel} from '@Components/email-maker-block-manager/components/image-block-panel';
import {EmailMakerBlock} from '@Components/email-maker-block-manager/components/email-maker-block';
import {getUniqueString} from '@Utils/string.util';
import {getBlocksFromCategories, getIconFromBlockId} from '@Libraries/email-component-template-library';
import {openAddVideoBlockChooser} from '@EmailMakerReact/lib/email-maker-media-modals-library';
import {noop} from '@Utils/general.util';
import {MobilePanelHeader} from '@Components/email-maker-block-manager/components/mobile-panel-header';
import {useIsMobileScreen} from '@Components/email-maker-block-manager/email-maker-block-manager-grapesjs-editor-context';
import {GA4EventName, GA4EventParamName} from '@Libraries/ga-events';
import type {ElementBlockPanelProps} from './elements-block-panel.types';
import {ElementsSubPanel} from './elements-block-panel.types';
import styles from './elements-block-panel.module.scss';

export function ElementsBlockPanel({
  blocksMap,
  showBackButton = false,
  headerLabel = '',
  onBackButton = noop,
  gjsEditor,
  subPanel,
  onSubPanelChange,
}: ElementBlockPanelProps): ReactElement {
  const BLOCK_CATEGORY = [window.PMW.emails.blocks.CATEGORY_ELEMENTS];
  const [localSubPanel, setLocalSubPanel] = useState<ElementsSubPanel>(ElementsSubPanel.ELEMENTS);
  const isMobileScreen = useIsMobileScreen();

  const isSubPanelControlled = subPanel !== undefined;
  const currentPanel = subPanel ?? localSubPanel;

  const changeSubPanel = (panel: ElementsSubPanel): void => {
    if (onSubPanelChange) {
      onSubPanelChange(panel);
    }
    if (!isSubPanelControlled) {
      setLocalSubPanel(panel);
    }
  };

  const getBlockLabel = (blockId: string): string => {
    switch (blockId) {
      case window.PMW.emails.blocks.ID_TABLE:
        return window.i18next.t('pmwjs_table');
      case window.PMW.emails.blocks.ID_STICKER:
        return window.i18next.t('pmwjs_sticker');
      case window.PMW.emails.blocks.ID_VIDEO:
        return window.i18next.t('pmwjs_video');
      case window.PMW.emails.blocks.ID_BUTTON:
        return window.i18next.t('pmwjs_button');
      case window.PMW.emails.blocks.ID_SOCIAL:
        return window.i18next.t('pmwjs_social_block');
      case window.PMW.emails.blocks.ID_ICONS:
        return window.i18next.t('pmwjs_icon');
      case window.PMW.emails.blocks.ID_DIVIDER:
        return window.i18next.t('pmwjs_divider');
      case window.PMW.emails.blocks.ID_COUNTDOWN:
        return window.i18next.t('pmwjs_countdown');
      case window.PMW.emails.blocks.CATEGORY_TEXT:
        return window.i18next.t('pmwjs_text');
      case window.PMW.emails.blocks.CATEGORY_IMAGE:
        return window.i18next.t('pmwjs_image');
      default:
        return 'Block label not found';
    }
  };

  const getBlockDescription = (blockId: string): string => {
    switch (blockId) {
      case window.PMW.emails.blocks.ID_TABLE:
        return window.i18next.t('pmwjs_table_block_description');
      case window.PMW.emails.blocks.ID_STICKER:
        return window.i18next.t('pmwjs_sticker_block_description');
      case window.PMW.emails.blocks.ID_VIDEO:
        return window.i18next.t('pmwjs_video_block_description');
      case window.PMW.emails.blocks.ID_BUTTON:
        return window.i18next.t('pmwjs_button_block_description');
      case window.PMW.emails.blocks.ID_SOCIAL:
        return window.i18next.t('pmwjs_social_block_description');
      case window.PMW.emails.blocks.ID_ICONS:
        return window.i18next.t('pmwjs_icon_block_description');
      case window.PMW.emails.blocks.ID_DIVIDER:
        return window.i18next.t('pmwjs_divider_block_description');
      case window.PMW.emails.blocks.ID_COUNTDOWN:
        return window.i18next.t('pmwjs_countdown_block_description');
      case window.PMW.emails.blocks.CATEGORY_TEXT:
        return window.i18next.t('pmwjs_text_block_description');
      case window.PMW.emails.blocks.CATEGORY_IMAGE:
        return window.i18next.t('pmwjs_image_block_description');
      default:
        return 'Block description not found';
    }
  };

  const getBlockGA4TrackingParam = (blockId: string): string => {
    switch (blockId) {
      case window.PMW.emails.blocks.ID_TABLE:
        return 'Table';
      case window.PMW.emails.blocks.ID_STICKER:
        return 'Sticker';
      case window.PMW.emails.blocks.ID_VIDEO:
        return 'Video';
      case window.PMW.emails.blocks.ID_BUTTON:
        return 'Button';
      case window.PMW.emails.blocks.ID_SOCIAL:
        return 'Social';
      case window.PMW.emails.blocks.ID_ICONS:
        return 'Icon';
      case window.PMW.emails.blocks.ID_DIVIDER:
        return 'Divider';
      case window.PMW.emails.blocks.ID_COUNTDOWN:
        return 'Countdown';
      default:
        return 'Unknown block';
    }
  };

  const getBlockVisualComponent = (blockId: string): ReactElement => {
    return (
      <>
        <Icon icon={getIconFromBlockId(blockId)} className={styles.icon} size={IconSize.SIZE_ICON_24} />
        <div className={styles.blockTextContainer}>
          <Text val={getBlockLabel(blockId)} size={TextSize.XSMALL} bold className="ct-neutral-primary" />
          <Text val={getBlockDescription(blockId)} size={TextSize.XXSMALL} className="ct-neutral-secondary" />
        </div>
      </>
    );
  };

  const getTextElementBlock = (): ReactElement => {
    return (
      <ClickableDiv
        className={`${styles.subSectionBlock} ${styles.block}`}
        onClick={(): void => {
          changeSubPanel(ElementsSubPanel.TEXT);
        }}
      >
        {getBlockVisualComponent(window.PMW.emails.blocks.CATEGORY_TEXT)}
      </ClickableDiv>
    );
  };

  const getImageElementBlock = (): ReactElement => {
    return (
      <ClickableDiv
        className={`${styles.subSectionBlock} ${styles.block}`}
        onClick={(): void => {
          changeSubPanel(ElementsSubPanel.IMAGE);
        }}
      >
        {getBlockVisualComponent(window.PMW.emails.blocks.CATEGORY_IMAGE)}
      </ClickableDiv>
    );
  };

  // Desktop mirrors the image blocks: clicking Video opens the source chooser popup and the placeholder is only added once a source needs it.
  const isDeferredVideoBlock = (block: Block): boolean => {
    return block.getId() === window.PMW.emails.blocks.ID_VIDEO && !isMobileScreen;
  };

  const getDefaultElementBlocks = (): ReactElement => {
    const elementBlocks: Block[] = getBlocksFromCategories(blocksMap, BLOCK_CATEGORY);

    return (
      <>
        {elementBlocks.map((block) => {
          return (
            <EmailMakerBlock
              block={block}
              extraClasses={styles.block}
              key={getUniqueString()}
              shouldAppendBlockOnClick={!isDeferredVideoBlock(block)}
              onClick={(): void => {
                if (isDeferredVideoBlock(block)) {
                  openAddVideoBlockChooser(gjsEditor);
                }
              }}
              onAddToCanvas={() => {
                trackGA4EventOnDefaultElementAdd(block.getId());
              }}
            >
              {getBlockVisualComponent(block.getId())}
            </EmailMakerBlock>
          );
        })}
      </>
    );
  };

  const getDefaultPanelContent = (): ReactElement => {
    return (
      <div className={styles.listItems}>
        {getMobilePanelHeader()}
        {getTextElementBlock()}
        {getImageElementBlock()}
        {getDefaultElementBlocks()}
      </div>
    );
  };

  const getTextElementPanelContent = (): ReactElement => {
    if (isSubPanelControlled) {
      return <TextBlockPanel blocksMap={blocksMap} />;
    }
    return (
      <TextBlockPanel
        blocksMap={blocksMap}
        showBackButton
        onBackButton={() => {
          changeSubPanel(ElementsSubPanel.ELEMENTS);
        }}
        headerLabel={window.i18next.t('pmwjs_text')}
      />
    );
  };

  const getImageElementPanelContent = (): ReactElement => {
    if (isSubPanelControlled) {
      return <ImageBlockPanel blocksMap={blocksMap} gjsEditor={gjsEditor} />;
    }
    return (
      <ImageBlockPanel
        blocksMap={blocksMap}
        showBackButton
        onBackButton={() => {
          changeSubPanel(ElementsSubPanel.ELEMENTS);
        }}
        headerLabel={window.i18next.t('pmwjs_image')}
        gjsEditor={gjsEditor}
      />
    );
  };

  const getMobilePanelHeader = (): ReactElement | null => {
    if (!isMobileScreen || !headerLabel) {
      return null;
    }

    return (
      <div className={styles.mobilePanelHeader}>
        <MobilePanelHeader headerLabel={headerLabel} showHandleBar showBackButton={showBackButton} onBackButton={onBackButton} />
      </div>
    );
  };

  const getPanelContent = (): ReactElement => {
    switch (currentPanel) {
      case ElementsSubPanel.TEXT:
        return getTextElementPanelContent();
      case ElementsSubPanel.IMAGE:
        return getImageElementPanelContent();
      case ElementsSubPanel.ELEMENTS:
      default:
        return getDefaultPanelContent();
    }
  };

  const trackGA4EventOnDefaultElementAdd = (blockId: string): void => {
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EMAILMAKER_ADD_ELEMENT, {[GA4EventParamName.TYPE]: getBlockGA4TrackingParam(blockId)});
  };

  return (
    <div
      className={`${styles.panelContent} ${isMobileScreen ? styles.mobile : ''} ${currentPanel === ElementsSubPanel.TEXT ? styles.textPanel : ''} ${
        currentPanel === ElementsSubPanel.IMAGE ? styles.imagePanel : ''
      }`}
    >
      {getPanelContent()}
    </div>
  );
}
